import React, { useEffect, useState } from 'react';
import { Card, Col, Form, InputGroup, Row, Spinner, Stack } from 'react-bootstrap';
import { BsCopy, BsGlobe } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa6';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as LedgerIcon } from '../../../../../assets/icons/ledger.svg';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { DetailSection, LabelText } from '../../../../../components/generic';
import { IconLink } from '../../../../../components/iconLink/IconLink';
import CheckmarkCell from '../../../../../components/tables/cellComponents/CheckmarkCell';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import YesNoCell from '../../../../../components/tables/cellComponents/YesNoCell';
import { useToast } from '../../../../../context/ToastContext';
import {
  useGetVenueLedgerQuery,
  useGetVenueQuery
} from '../../../../../services/endpoints/places/venue';
import { getCurrentUser } from '../../../../../services/helper';
import { SFCVenue } from '../../../../../types/place.types';
import { canViewVenueReports, canViewVenues } from '../../../../../utils/rbac';

const currentUser = getCurrentUser();

export const VenueDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const userHasAccess = canViewVenues(currentUser);
  const { data, error, isLoading } = useGetVenueQuery(+params.id!, {
    skip: !userHasAccess
  });
  const venueLocationId = data?.venue?.location_id ?? null;
  const canViewReports = canViewVenueReports(currentUser, venueLocationId);
  const {
    data: ledger,
    error: ledgerError,
    isLoading: isLoadingLedger
  } = useGetVenueLedgerQuery(
    { id: +params.id! },
    { skip: !userHasAccess || params.id === undefined || !canViewReports }
  );
  const [venue, setVenue] = useState<SFCVenue>();

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading venue', 'error');
    }
    if (canViewReports && ledgerError && !isLoadingLedger) {
      addToast('Error while loading venue ledger', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, ledgerError, isLoadingLedger, canViewReports]);

  useEffect(() => {
    if (data) {
      setVenue(data.venue);
    }
  }, [data]);

  if (!userHasAccess) {
    return <Forbidden />;
  }

  if (isLoading || (canViewReports && isLoadingLedger) || !venue) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  const showReportsSection = canViewVenueReports(currentUser, venue.location_id);

  const publicSearchParams = new URLSearchParams({ venue_id: String(venue.id) });
  if (venue.latitude != null && venue.longitude != null) {
    publicSearchParams.set('origin', `${venue.latitude}_${venue.longitude}`);
  }
  const publicSearchUrl = `${window.location.origin}/sessions?${publicSearchParams.toString()}`;

  const copyPublicSearchLink = async () => {
    try {
      await navigator.clipboard.writeText(publicSearchUrl);
      addToast('Public session search link copied', 'success');
    } catch {
      addToast('Unable to copy link', 'error');
    }
  };

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(returnTo ?? '/admin/places/venues')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1 text-truncate"
            style={{ color: '#888888', fontSize: '1rem', maxWidth: 'min(100%, calc(100vw - 4rem))' }}
          >
            {venue?.title ?? 'Venues List'}
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">
            {venue?.title ?? `Venue #${venue?.id}`}
          </h2>
        </Stack>
        {!currentUser?.isInstructor && (
          <Stack
            direction="horizontal"
            gap={1}
            className="d-none d-lg-flex"
          >
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() => navigate(`/admin/places/venues/${venue?.id}/edit`)}
            >
              Edit venue
            </SimpleButton>
          </Stack>
        )}
      </div>
      <div className="w-100">
        <Stack
          gap={4}
          className="px-3 py-4 px-lg-4"
        >
          <Row className="g-4">
            <Col
              xs={12}
              lg={showReportsSection ? 8 : 12}
            >
              <DetailSection title="Details">
                <LabelText label="Title:">{venue?.title}</LabelText>
                <LabelText label="Active:">
                  <YesNoCell value={venue?.is_active} />
                </LabelText>
                {!currentUser?.isInstructor && (
                  <LabelText label="Location:">
                    <LinkCell
                      content={venue?.location?.title ?? 'No location title'}
                      url={`/admin/places/locations/${venue?.location?.id}`}
                    />
                  </LabelText>
                )}
                {!currentUser?.isInstructor && (
                  <LabelText label="Organizations:">
                    {venue?.organizations?.map((o) => (
                      <React.Fragment key={o.id}>
                        <LinkCell
                          content={o.name}
                          url={`/admin/places/organizations/${o.id}`}
                        />
                        ,&nbsp;
                      </React.Fragment>
                    ))}
                  </LabelText>
                )}
                {!currentUser?.isInstructor && (
                  <LabelText label="Phone:">{venue?.phone!}</LabelText>
                )}
                <LabelText label="Address:">{venue?.address!}</LabelText>
                <LabelText label="Address2:">{venue?.address2!}</LabelText>
                <LabelText label="City:">{venue?.city!}</LabelText>
                <LabelText label="State:">{venue?.state!}</LabelText>
                <LabelText label="Zip:">{venue?.zip!}</LabelText>
                <LabelText label="Latitude:">{venue?.latitude!}</LabelText>
                <LabelText label="Longitude:">{venue?.longitude!}</LabelText>
                {!currentUser?.isInstructor && (
                  <LabelText
                    label="Notes:"
                    direction="vertical"
                  >
                    {venue?.notes!}
                  </LabelText>
                )}
                {!currentUser?.isInstructor && (
                  <LabelText
                    label="Facility Use Payment Mailing Details:"
                    direction="vertical"
                  >
                    {venue?.facility_use_fee_payment_details!}
                  </LabelText>
                )}
                {!currentUser?.isInstructor && (
                  <LabelText label="Session History:">
                    <LinkCell
                      content={`${venue?.course_count} Session${venue?.course_count !== 1 ? 's' : ''}`}
                      url={`/admin/schedule/sessions?scope=all&venue_id=${venue?.id}`}
                    />
                  </LabelText>
                )}
                {!currentUser?.isInstructor && (
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{ color: 'rgba(94, 100, 105, .5)' }}
                    >
                      Public session search
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        readOnly
                        value={publicSearchUrl}
                        style={{ backgroundColor: '#f5f6f7' }}
                        onFocus={(e) => e.currentTarget.select()}
                      />
                      <InputGroup.Text
                        className="py-0"
                        style={{ backgroundColor: '#f5f6f7' }}
                      >
                        <button
                          type="button"
                          className="btn p-1 text-secondary"
                          style={{ border: 'none', boxShadow: 'none', lineHeight: 1 }}
                          aria-label="Copy public session search link"
                          onClick={copyPublicSearchLink}
                        >
                          <BsCopy size={18} />
                        </button>
                      </InputGroup.Text>
                      <InputGroup.Text
                        className="py-0"
                        style={{ backgroundColor: '#f5f6f7' }}
                      >
                        <a
                          className="btn p-1 text-secondary"
                          style={{ border: 'none', boxShadow: 'none', lineHeight: 1 }}
                          href={publicSearchUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open public session search"
                        >
                          <BsGlobe size={18} />
                        </a>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                )}
                {canViewReports && (
                  <LabelText label="Revenue History:">
                    <LinkCell
                      content={
                        <CurrencyCell
                          value={String(
                            Object.values(ledger?.ledger?.revenue ?? {}).reduce(
                              (acc, curr) => acc + curr,
                              0
                            )
                          )}
                        />
                      }
                      url={`/admin/places/venues/${venue?.id}/ledger`}
                    />
                  </LabelText>
                )}
              </DetailSection>
            </Col>
            {showReportsSection && (
              <Col
                xs={12}
                lg={4}
              >
                <DetailSection title="Reports">
                  <IconLink
                    icon={<LedgerIcon />}
                    text={'Summary Session Financials'}
                    url={`/admin/places/venues/${venue.id}/ledger`}
                  />
                </DetailSection>
              </Col>
            )}
          </Row>
          {!currentUser?.isInstructor && (
            <DetailSection title="Contacts">
              <Row className="g-3">
                {venue.contacts.map((c) => (
                  <Col
                    key={c.id}
                    xs={12}
                    md={6}
                    lg={6}
                    xl={5}
                    xxl={4}
                  >
                  <Card className="text-left shadow">
                    <Card.Body>
                      <Card.Text className={'h2 text-capitalize'}>{c.full_name}</Card.Text>
                      <br />
                      <LabelText label="Email:">
                        <span style={{ wordBreak: 'break-all' }}>{c.email}</span>
                      </LabelText>
                      <LabelText label="Receives Rosters:">
                        <CheckmarkCell value={c.should_be_sent_roster} />
                      </LabelText>
                      <LabelText label="Receives Invoices:">
                        <CheckmarkCell value={c.should_be_sent_invoices} />
                      </LabelText>
                      <LabelText label="Receives Bulk Welcomes:">
                        <CheckmarkCell value={c.should_be_sent_bulk_welcomes} />
                      </LabelText>
                      <LabelText label="Receives Bulk Thanks:">
                        <CheckmarkCell value={c.should_be_sent_bulk_thanks} />
                      </LabelText>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              </Row>
            </DetailSection>
          )}
        </Stack>
      </div>
    </>
  );
};
